import React, { useState, useEffect } from "react";
import CommentList from "../../components/lost/CommentList";
import Badge from "../../components/lost/Badge";
import "./ItemDetail.css";
import { supabase } from "../../lib/supabaseClient";

// 분실물 상세 페이지 컴포넌트
const ItemDetailPage = ({ itemId, onClose }) => {

  // 게시글 정보
  const [item, setItem] = useState(null);

  // 현재 로그인한 사용자 정보
  const [currentUserInfo, setCurrentUserInfo] = useState(null);

  // 수정 모드 여부
  const [isEditing, setIsEditing] = useState(false);

  // 수정 폼 데이터
  const [editForm, setEditForm] = useState({
    title: "",
    category: "",
    location: "",
    description: "",
    status: "",
    image_url: "",
  });

  // 새로 선택한 이미지 파일
  const [selectedFile, setSelectedFile] = useState(null);

  // 게시글 상세 정보 조회
  useEffect(() => {
    const fetchItemDetail = async () => {
      try {
        // 게시글 정보 조회
        const { data: itemData, error: itemError } = await supabase
          .from("lost_items")
          .select("*")
          .eq("id", itemId)
          .single();

        if (itemError) throw itemError;

        setItem(itemData);

        // 현재 로그인 사용자 조회
        const {
          data: { user: authUser },
        } = await supabase.auth.getUser();

        if (authUser) {
          // 사용자 정보 조회
          const { data: userData, error: userError } = await supabase
            .from("users")
            .select("id, role")
            .eq("email", authUser.email)
            .single();

          if (!userError && userData) {
            setCurrentUserInfo(userData);
          }
        }
      } catch (error) {
        console.error("상세 데이터 로딩 에러:", error.message);
      }
    };

    if (itemId) fetchItemDetail();
  }, [itemId]);

  // 게시글 삭제
  const handleDelete = async () => {
    const isConfirm = window.confirm(
      "정말 이 게시글을 삭제하시겠습니까? (댓글도 함께 삭제됩니다.)"
    );

    if (!isConfirm) return;

    try {
      const { error } = await supabase
        .from("lost_items")
        .delete()
        .eq("id", itemId);

      if (error) throw error;

      alert("게시글이 삭제되었습니다.");
      window.location.reload();
    } catch (error) {
      console.error("삭제 실패:", error.message);
      alert("삭제에 실패했습니다: " + error.message);
    }
  };

  // 수정 버튼 클릭 시 기존 정보 불러오기
  const handleEditClick = () => {
    setEditForm({
      title: item.title || "",
      category: item.category || "",
      location: item.location || "",
      description: item.description || "",
      status: item.status || (item.item_type === "분실" ? "찾는 중" : "보관 중"),
      image_url: item.image_url || "",
    });

    setSelectedFile(null);
    setIsEditing(true);
  };

  // 게시글 수정 저장
  const handleUpdateSubmit = async () => {
    try {
      const isMyPost = currentUserInfo?.id === item.user_id;

      // 작성자 확인
      if (!isMyPost) {
        alert("작성자만 수정할 수 있습니다.");
        return;
      }

      let finalImageUrl = editForm.image_url;

      // 이미지 변경 시 Storage 업로드
      if (selectedFile) {
        const fileExt = selectedFile.name.split(".").pop();
        const fileName = `${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("lost_images")
          .upload(fileName, selectedFile);

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from("lost_images")
          .getPublicUrl(fileName);

        finalImageUrl = urlData.publicUrl;
      }

      // 게시글 정보 수정
      const { data, error } = await supabase
        .from("lost_items")
        .update({
          title: editForm.title,
          category: editForm.category,
          location: editForm.location,
          description: editForm.description,
          status: editForm.status,
          image_url: finalImageUrl,
        })
        .eq("id", itemId)
        .select()
        .single();

      if (error) throw error;

      setItem(data);
      setIsEditing(false);

      alert("게시글이 수정되었습니다!");
    } catch (error) {
      console.error("수정 실패:", error.message);
      alert("수정에 실패했습니다: " + error.message);
    }
  };

  // 데이터 로딩 중 화면
  if (!item) {
    return (
      <div className="lost-modal-overlay" onClick={onClose}>
        <div className="lost-modal-content" onClick={(e) => e.stopPropagation()}>
          <p>데이터를 불러오는 중입니다...</p>
        </div>
      </div>
    );
  }

  // 권한 확인
  const isMyPost = currentUserInfo?.id === item.user_id;
  const isAdmin = currentUserInfo?.role === "admin";

  // 수정 가능 여부
  const canEditPost = isMyPost;

  // 삭제 가능 여부
  const canDeletePost = isMyPost || isAdmin;

  return (
    <div className="lost-modal-overlay" onClick={onClose}>
      <div className="lost-modal-content" onClick={(e) => e.stopPropagation()}>

        {/* 닫기 버튼 */}
        <button className="close-button" onClick={onClose}>
          ✕
        </button>

        {isEditing ? (
          // 게시글 수정 화면
          <div className="edit-mode-container">

            <h3>게시글 수정</h3>

            {/* 상태 선택 */}
            <div className="edit-form-group">
              <label className="edit-form-label">물건 상태 설정</label>

              <select
                className="edit-form-input"
                value={editForm.status}
                onChange={(e) =>
                  setEditForm({ ...editForm, status: e.target.value })
                }
              >
                {item.item_type === "분실" && (
                  <>
                    <option value="찾는 중">찾는 중</option>
                    <option value="완료">주인찾음 완료</option>
                  </>
                )}

                {item.item_type === "습득" && (
                  <>
                    <option value="보관 중">보관 중</option>
                    <option value="완료">주인찾음 완료</option>
                  </>
                )}
              </select>
            </div>

            {/* 이미지 변경 */}
            <div className="edit-form-group">
              <label className="edit-form-label">사진 변경</label>

              <input
                type="file"
                accept="image/*"
                className="edit-file-input"
                onChange={(e) => setSelectedFile(e.target.files[0])}
              />
            </div>

            {/* 제목 입력 */}
            <input
              type="text"
              className="edit-form-input"
              value={editForm.title}
              onChange={(e) =>
                setEditForm({ ...editForm, title: e.target.value })
              }
              placeholder="제목을 입력하세요"
            />

            {/* 카테고리 및 장소 */}
            <div className="edit-form-row">
              <select
                className="edit-form-input half"
                value={editForm.category}
                onChange={(e) =>
                  setEditForm({ ...editForm, category: e.target.value })
                }
              >
                <option value="전자기기">전자기기</option>
                <option value="지갑/신분증">지갑/신분증</option>
                <option value="의류/잡화">의류/잡화</option>
                <option value="기타">기타</option>
              </select>

              <input
                type="text"
                className="edit-form-input half"
                value={editForm.location}
                onChange={(e) =>
                  setEditForm({ ...editForm, location: e.target.value })
                }
                placeholder="장소"
              />
            </div>

            {/* 상세 설명 */}
            <textarea
              className="edit-form-textarea"
              value={editForm.description}
              onChange={(e) =>
                setEditForm({ ...editForm, description: e.target.value })
              }
              placeholder="상세 설명을 입력하세요"
            />

            {/* 수정 버튼 */}
            <div className="edit-action-buttons">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="cancel-btn"
              >
                취소
              </button>

              <button
                type="button"
                onClick={handleUpdateSubmit}
                className="save-btn"
              >
                저장 완료
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* 게시글 제목 */}
            <div className="detail-header">
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Badge type={item.status === "완료" ? "주인찾음" : item.item_type} />
                <h2 className="detail-title">{item.title}</h2>
              </div>
            </div>

            <div className="detail-info-grid">
              <div className="detail-info-item">
                <span>분류</span>
                <strong>{item.category || "-"}</strong>
              </div>

              <div className="detail-info-item">
                <span>장소</span>
                <strong>{item.location || "-"}</strong>
              </div>

              <div className="detail-info-item">
                <span>날짜</span>
                <strong>{item.item_date || "-"}</strong>
              </div>

              <div className="detail-info-item">
                <span>상태</span>
                <strong>{item.status || "-"}</strong>
              </div>
            </div>

            <div className="detail-description">
              {item.description || "상세 설명이 없습니다."}
            </div>

            {item.image_url && (
              <div className="detail-image-box">
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="detail-image"
                />
              </div>
            )}

            {/* 수정 및 삭제 버튼 */}
            {canDeletePost && (
              <div style={{ display: "flex", gap: "20px", marginTop: "8px", marginBottom: "4px", justifyContent: "flex-end" }}>
                {canEditPost && (
                  <span
                    onClick={handleEditClick}
                    style={{ fontSize: "16px", color: "#888", cursor: "pointer" }}
                  >
                    수정
                  </span>
                )}

                <span
                  onClick={handleDelete}
                  style={{ fontSize: "16px", color: "#fa5252", cursor: "pointer" }}
                >
                  삭제
                </span>
              </div>
            )}

            {/* 댓글 목록 */}
            <CommentList
              itemId={itemId}
              itemAuthorId={item.user_id}
              currentUserInfo={currentUserInfo}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ItemDetailPage;